import { clsx } from 'clsx';
import type { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  id?: string;
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export function Card({ children, className, padding = 'md', hover = false, id }: CardProps) {
  return (
    <div
      id={id}
      className={clsx(
        'bg-gradient-to-br from-[var(--color-bg-primary)] to-[var(--color-bg-primary)]/95',
        'rounded-xl border border-[var(--color-border)]',
        'shadow-sm transition-all duration-300',
        hover && 'hover:shadow-lg hover:shadow-[var(--color-accent)]/5 hover:border-[var(--color-accent)]/30 hover:-translate-y-0.5',
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  icon?: ReactNode;
}

export function CardHeader({ title, subtitle, action, icon }: CardHeaderProps) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        {icon && (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
            {icon}
          </div>
        )}
        <div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-[var(--color-text-muted)]">{subtitle}</p>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  loading = false,
  className,
}: StatCardProps) {
  const isPositive = change && change > 0;
  const isNegative = change && change < 0;

  return (
    <Card className={clsx('relative overflow-hidden', className)}>
      {loading ? (
        <div className="space-y-3">
          <div className="h-4 w-24 skeleton rounded" />
          <div className="h-8 w-32 skeleton rounded" />
          <div className="h-3 w-20 skeleton rounded" />
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--color-text-muted)]">
                {title}
              </p>
              <p className="mt-2 text-3xl font-bold text-[var(--color-text-primary)]">
                {value}
              </p>
              {change !== undefined && (
                <p
                  className={clsx(
                    'mt-2 text-sm font-medium flex items-center gap-1',
                    isPositive && 'text-emerald-500',
                    isNegative && 'text-red-500',
                    !isPositive && !isNegative && 'text-[var(--color-text-muted)]'
                  )}
                >
                  <span>{isPositive ? '+' : ''}{change.toFixed(1)}%</span>
                  {changeLabel && (
                    <span className="text-[var(--color-text-muted)]">
                      {changeLabel}
                    </span>
                  )}
                </p>
              )}
            </div>
            {icon && (
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-accent)]/10 text-[var(--color-accent)]">
                {icon}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
